import { AttachFile } from '@mui/icons-material';
import { Chip, Grid, Stack, Typography } from '@mui/material';

import { Or2Card } from '@/components/Surface/Or2Card';
import { graphql, type DocumentType } from '@/gql';
import { UserAvatar } from '@/modules/user/components/UserAvatar';
import { extractConnectionProps } from '@/utils/queries/queryExtracts';
import { Or2Link } from '@/components/navigation/Or2Link';

const ASSISTANT_USER_INPUT_BUBBLE_PROMPT_FRAGMENT = graphql(`
  fragment assistantUserInputBubblePromptFields on OrtooGptConversationItemPrompt {
    text
    fileIdsConnection {
      edges {
        node {
          _id
          _exists
          filename
        }
      }
    }
  }
`);

const ASSISTANT_USER_INPUT_BUBBLE_USER_FRAGMENT = graphql(`
  fragment assistantUserInputBubbleUserFields on User {
    ...userImageFields
  }
`);

interface Props {
  prompt: DocumentType<typeof ASSISTANT_USER_INPUT_BUBBLE_PROMPT_FRAGMENT>;
  user: DocumentType<typeof ASSISTANT_USER_INPUT_BUBBLE_USER_FRAGMENT>;
}

export const AssistantUserInputBubble = ({ prompt, user }: Props) => {
  const { text } = prompt;

  const files = extractConnectionProps(prompt.fileIdsConnection);

  return (
    <Grid
      container
      spacing={1}
      alignSelf="flex-end"
      justifyContent="end"
      width={'100%'}
      flexWrap={'nowrap'}
    >
      <Grid>
        <Or2Card
          or2Variant="blank"
          sx={{ padding: 2, backgroundColor: 'secondary.main' }}
        >
          <Grid container direction="column" spacing={0}>
            <Grid>
              <Typography fontWeight="bold" variant="body1" align="left">
                You
              </Typography>
            </Grid>
            <Grid>
              <Typography variant="body1" align="left">
                {text}
              </Typography>
            </Grid>
            {files.length > 0 && (
              <Grid sx={{ mt: 1 }}>
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {files?.map((file) =>
                    file._exists ? (
                      <Or2Link
                        key={file._id}
                        href={`/document/${file._id}/view`}
                        color="inherit"
                        isExternal
                      >
                        <Chip
                          label={file.filename || 'Unknown file'}
                          icon={<AttachFile />}
                          size="small"
                        />
                      </Or2Link>
                    ) : (
                      <Chip
                        key={file._id}
                        label={'Unknown file (possibly deleted)'}
                        icon={<AttachFile />}
                        size="small"
                        disabled
                      />
                    ),
                  )}
                </Stack>
              </Grid>
            )}
          </Grid>
        </Or2Card>
      </Grid>
      <Grid minWidth={32}>
        <UserAvatar user={user} size={32} />
      </Grid>
    </Grid>
  );
};
